import React, { useCallback } from "react";
import { gql, useQuery } from "@apollo/client";
import {
  GetPointsHistoryQueryQuery,
  GetPointsHistoryQueryQueryVariables,
} from "../../../schemaTypes";

export const getPointsHistoryQuery = gql`
  query GetPointsHistoryQuery(
    $offset: Int!
    $limit: Int!
    $typeFilter: String
  ) {
    getPointsHistory(
      offset: $offset
      limit: $limit
      typeFilter: $typeFilter
    ) {
      createdAt
      pointHistory {
        action
        category
        point
        referredFirstName
        referredLastName
        referredProfileImage
        createdAt
        type
      }
    }
  }
`;

export interface PointHistoryItem {
  action: string;
  category: string;
  point: string;
  referredFirstName?: string | null;
  referredLastName?: string | null;
  referredProfileImage?: string | null;
  createdAt: string;
}

export interface GroupedPointHistory {
  createdAt: string;
  pointHistory: PointHistoryItem[];
}

export interface WithPointsHistory {
  pointHistory: GroupedPointHistory[];
  loading: boolean;
  loadMore: () => void;
  hasMorePointsHistory: boolean;
}

interface Props {
  variables: GetPointsHistoryQueryQueryVariables;
  children: (data: WithPointsHistory) => React.ReactElement | null;
}

export const PointsHistoryController: React.FC<Props> = ({
  children,
  variables,
}) => {
  const { data, loading, fetchMore } = useQuery<
    GetPointsHistoryQueryQuery,
    GetPointsHistoryQueryQueryVariables
  >(getPointsHistoryQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const pointHistoryData: GroupedPointHistory[] = (
    data?.getPointsHistory ?? []
  ).filter((item): item is GroupedPointHistory => item !== null);

  const hasMorePointsHistory =
    pointHistoryData.length % variables.limit === 0 &&
    pointHistoryData.length > variables.offset;

  const loadMore = useCallback(() => {
    fetchMore({
      variables: {
        ...variables,
        offset: pointHistoryData.length,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (
          !fetchMoreResult ||
          !fetchMoreResult.getPointsHistory ||
          !prev ||
          !prev.getPointsHistory
        ) {
          return prev;
        }

        return {
          ...prev,
          getPointsHistory: [
            ...prev.getPointsHistory,
            ...fetchMoreResult.getPointsHistory,
          ],
        };
      },
    });
  }, [fetchMore, variables, pointHistoryData.length]);

  return children({
    pointHistory: pointHistoryData,
    loading,
    loadMore,
    hasMorePointsHistory,
  });
};